import {format} from "date-fns";
import {useTranslation} from "react-i18next";

import {useViewAmbientContext} from "./Ambients";
import {useViewImageContext} from "./Images/ViewImageContext";
import {getImageTypeTranslationKey} from "./Images/imageTypeUtils";
import {useViewLandContext} from "./ViewLandContext";
import {PLANET_HD_V1_ENABLED} from "../../../../../config/featureFlags";

const getImageTypeLabel = (imageType, t) => {
  return t(`images:selector.${getImageTypeTranslationKey(imageType)}`);
};

const getDatesStatusLabel = (datesState, selectedDate, imageType, t) => {
  if (imageType === "altimetry") {
    return t("images:selector.altimetry");
  }

  if (imageType === "soil_map") {
    return t("images:selector.soil_map");
  }

  if (imageType === "yield_map") {
    return t("images:selector.yield_map");
  }

  if (selectedDate) {
    return `${format(selectedDate, "dd/MM/yyyy")} - ${getImageTypeLabel(imageType, t)}`;
  }

  if (datesState.status === "loading") {
    return t("images:status.searching_dates");
  }

  if (datesState.status === "ready") {
    if (datesState.availableDates > 0 && datesState.cloudyDates > 0) {
      return t("images:status.clear_and_cloudy", {
        clear: datesState.availableDates,
        cloudy: datesState.cloudyDates,
      });
    }

    if (datesState.availableDates > 0) {
      return t("images:status.ready_dates", {count: datesState.availableDates});
    }

    if (datesState.cloudyDates > 0) {
      return t("images:status.cloudy_dates", {count: datesState.cloudyDates});
    }
  }

  if (datesState.status === "empty") {
    return t("images:status.no_dates");
  }

  if (datesState.status === "error") {
    return t("images:status.load_error");
  }

  return t("images:status.choose_date_index");
};

const getAmbientStatusLabel = (ambientSectionState, t) => {
  if (ambientSectionState === "loading") {
    return t("ambient:view.status.reviewing_land");
  }

  if (ambientSectionState === "processing") {
    return t("ambient:view.status.processing");
  }

  if (ambientSectionState === "failed") {
    return t("ambient:view.status.failed");
  }

  if (ambientSectionState === "image_ready" || ambientSectionState === "image_hd_ready") {
    return t("ambient:view.status.image_ready");
  }

  if (ambientSectionState === "ready") {
    return t("ambient:view.status.historical_ready");
  }

  return t("ambient:view.status.not_created");
};

const getLayersStatusLabel = (ambientSectionState, t) => {
  if (ambientSectionState === "loading") {
    return t("layers:status.reviewing_downloads");
  }

  if (ambientSectionState === "image_ready" || ambientSectionState === "image_hd_ready") {
    return t("layers:status.image_ready");
  }

  if (ambientSectionState === "ready") {
    return t("layers:status.historical_ready");
  }

  if (ambientSectionState === "processing") {
    return t("layers:status.processing");
  }

  return t("layers:status.land_ready");
};

const getSoilMapHint = (soilMapState, t) => {
  if (soilMapState.loading) {
    return t("layers:hints.soil_map_loading");
  }

  if (soilMapState.status === "idle") {
    return t("layers:hints.soil_map_on_demand");
  }

  if (soilMapState.status === "available") {
    return t("layers:hints.soil_map_ready");
  }

  if (soilMapState.status === "no_coverage") {
    return t("layers:hints.soil_map_no_coverage");
  }

  return t("layers:hints.soil_map_unavailable");
};

export const useLandOperationSummary = () => {
  const {t} = useTranslation();
  const viewLandContext = useViewLandContext();
  const viewImageContext = useViewImageContext();
  const {ambientSummary, canAccessHistoricalHd} = useViewAmbientContext();
  const landId = viewLandContext.land?.land_id;
  const imageSnapshot = viewImageContext.getLandImageSnapshot(landId);
  const soilMapState = viewImageContext.getLandSoilMapState(landId);
  const selectedDateLabel = imageSnapshot.selectedDate
    ? format(imageSnapshot.selectedDate, "dd/MM/yyyy")
    : t("images:date_selector");
  const activeImageTypeLabel = getImageTypeLabel(imageSnapshot.imageType, t);
  const imageStatusLabel = getDatesStatusLabel(
    imageSnapshot.datesState,
    imageSnapshot.selectedDate,
    imageSnapshot.imageType,
    t
  );
  const ambientStatusLabel = getAmbientStatusLabel(ambientSummary.sectionState, t);
  const layersStatusLabel = getLayersStatusLabel(ambientSummary.sectionState, t);
  const soilMapCanBeRequested = ["idle", "loading", "available"].includes(
    soilMapState.status
  );

  return {
    image: {
      ...imageSnapshot,
      selectedDateLabel,
      activeImageTypeLabel,
      statusLabel: imageStatusLabel,
    },
    ambient: {
      ...ambientSummary,
      statusLabel: ambientStatusLabel,
    },
    layers: {
      statusLabel: layersStatusLabel,
      options: [
        {
          value: "layer",
          label: t("layers:layer_type.land"),
          enabled: true,
          hint: t("layers:hints.land_available"),
        },
        {
          value: "ambient",
          label: t("layers:layer_type.ambient"),
          enabled: ambientSummary.hasHistoricalAmbient,
          hint: ambientSummary.hasHistoricalAmbient
            ? t("layers:hints.historical_ready")
            : t("layers:hints.historical_missing"),
        },
        {
          value: "image_ambient",
          label: t("layers:layer_type.image_ambient"),
          enabled: ambientSummary.hasImageAmbient,
          hint: ambientSummary.hasImageAmbient
            ? t("layers:hints.image_ready")
            : t("layers:hints.image_missing"),
        },
        ...(PLANET_HD_V1_ENABLED ? [
          ...(canAccessHistoricalHd ? [{
            value: "ambient_hd",
            label: t("layers:layer_type.ambient_hd"),
            enabled: ambientSummary.hasHistoricalHdAmbient,
            hint: ambientSummary.hasHistoricalHdAmbient
              ? t("layers:hints.historical_hd_ready")
              : t("layers:hints.historical_hd_missing"),
          }] : []),
          {
            value: "image_ambient_hd",
            label: t("layers:layer_type.image_ambient_hd"),
            enabled: ambientSummary.hasImageAmbientHd,
            hint: ambientSummary.hasImageAmbientHd
              ? t("layers:hints.image_hd_ready")
              : t("layers:hints.image_hd_missing"),
          },
        ] : []),
        {
          value: "altimetry",
          label: t("layers:layer_type.altimetry"),
          enabled: true,
          hint: t("layers:hints.altimetry_available"),
        },
        {
          value: "soil_map",
          label: t("layers:layer_type.soil_map"),
          enabled: soilMapCanBeRequested,
          hint: getSoilMapHint(soilMapState, t),
        },
      ],
    },
  };
};
